import { z } from 'zod';
import { narrowWindow } from '@/lib/format/format';
import { categoryFromKey, CATEGORY_KEYS } from '@/features/events/vocab';
import { tabWindow, type EventSort, type OrganizationEventsQuery } from './api';
import { RUNNER_SORTS, type Direction, type RunnerSort } from './runners';
import { EVENT_TABS, type EventTab } from './vocab';

export const ORGANIZER_EVENTS_PAGE_SIZE = 25;

/** The API refuses a longer keyword. */
export const KEYWORD_MAX = 100;

const SORTS = ['date', 'name', 'city'] as const satisfies readonly EventSort[];

/** An organization’s events list, kept in the URL. The default order stays out of it. */
export const organizerEventsSearchSchema = z.object({
  status: z.enum(EVENT_TABS).default('upcoming').catch('upcoming'),
  q: z.string().trim().min(1).max(KEYWORD_MAX).optional().catch(undefined),
  category: z
    .string()
    .refine((key) => CATEGORY_KEYS.includes(key))
    .optional()
    .catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  sort: z.enum(SORTS).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
});

export type OrganizerEventsSearch = z.output<typeof organizerEventsSearchSchema>;

const FILTERS = ['q', 'category', 'from', 'to'] as const satisfies (keyof OrganizerEventsSearch)[];

export const noFilters: Partial<OrganizerEventsSearch> = Object.fromEntries(
  FILTERS.map((key) => [key, undefined]),
);

export const hasFilters = (search: OrganizerEventsSearch): boolean =>
  FILTERS.some((key) => search[key] !== undefined);

/** Dates run soonest first on Upcoming and most recent first on Past; names and cities A–Z. */
const naturalDirection = (sort: EventSort, tab: EventTab): Direction =>
  sort === 'date' && tab === 'past' ? 'desc' : 'asc';

/** The order the list is in. */
export function sortOf(search: OrganizerEventsSearch): { key: EventSort; dir: Direction } {
  const key = search.sort ?? 'date';
  return { key, dir: search.dir ?? naturalDirection(key, search.status) };
}

/** A column header's click: the same column turns around, another starts in its natural order. */
export function sortBy(
  search: OrganizerEventsSearch,
  key: EventSort,
): Partial<OrganizerEventsSearch> {
  const current = sortOf(search);
  const natural = naturalDirection(key, search.status);
  const dir = key === current.key ? (current.dir === 'asc' ? 'desc' : 'asc') : natural;
  return {
    sort: key === 'date' ? undefined : key,
    dir: dir === natural ? undefined : dir,
    page: 1,
  };
}

/** Another tab keeps a name or city order; a date order takes the tab's own. */
export const toTab = (
  search: OrganizerEventsSearch,
  tab: EventTab,
): Partial<OrganizerEventsSearch> => ({
  status: tab,
  page: 1,
  ...(search.sort ? {} : { dir: undefined }),
});

/** The list read for this view. */
export function toOrganizationEventsQuery(
  search: OrganizerEventsSearch,
  today: string,
): OrganizationEventsQuery {
  const sort = sortOf(search);
  return {
    window: narrowWindow(tabWindow(search.status, today), search.from, search.to),
    keyword: search.q,
    category: search.category ? categoryFromKey(search.category) : undefined,
    sort: sort.key,
    descending: sort.dir === 'desc',
    page: search.page,
    size: ORGANIZER_EVENTS_PAGE_SIZE,
  };
}

export const RUNNERS_PAGE_SIZE = 50;

/** One event's interested runners, kept in the URL; the default order and page stay out of it. */
export const runnersSearchSchema = z.object({
  q: z.string().trim().min(1).max(KEYWORD_MAX).optional().catch(undefined),
  sort: z.enum(RUNNER_SORTS).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.coerce.number().int().min(2).optional().catch(undefined),
});

export type RunnersSearch = z.output<typeof runnersSearchSchema>;

/** Names and emails run A–Z, ages youngest first. */
export function runnersSortOf(search: RunnersSearch): { key: RunnerSort; dir: Direction } {
  return { key: search.sort ?? 'name', dir: search.dir ?? 'asc' };
}

/** A column header's click: the same column turns around, another starts A–Z (youngest first). */
export function runnersSortBy(search: RunnersSearch, key: RunnerSort): Partial<RunnersSearch> {
  const current = runnersSortOf(search);
  const dir = key === current.key && current.dir === 'asc' ? 'desc' : undefined;
  return { sort: key === 'name' ? undefined : key, dir, page: undefined };
}

export const runnersPage = (search: RunnersSearch): number => search.page ?? 1;
